/**
 * ReviewInbox — popover content opened from NotificationsChip.
 *
 * Two tabs: "Pending review" (approvals assigned to me) and "Activity"
 * (recent decisions + jobs). Inline approve / reject actions on the
 * pending tab — reject opens a tiny inline reason field before sending.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, Close, Visibility } from '@mui/icons-material';
import { api } from '../../services/api';
import { parseApiError } from '../../utils/sanitizeError';

const TAB_PENDING = 0;
const TAB_ACTIVITY = 1;

const isPendingApproval = (n) => n.source_type === 'approval';

const ReviewInbox = ({ onNavigate, onClose }) => {
  const [tab, setTab] = useState(TAB_PENDING);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getNotifications({ limit: 50 });
      const list = Array.isArray(res?.data?.notifications)
        ? res.data.notifications
        : [];
      setNotifications(list);
    } catch (e) {
      setError(parseApiError(e, 'Failed to load notifications.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mark-read on close.
  useEffect(() => {
    return () => {
      api.markNotificationsRead(null, new Date().toISOString()).catch(() => {});
    };
  }, []);

  const handleApprove = async (n) => {
    try {
      await api.decideApproval(n.source_id, 'approve', null);
      setNotifications((prev) => prev.filter((p) => p.source_id !== n.source_id));
    } catch (e) {
      setError(parseApiError(e, 'Approve failed.'));
    }
  };

  const handleStartReject = (n) => {
    setRejectingId(n.source_id);
    setRejectReason('');
  };

  const handleConfirmReject = async (n) => {
    if (!rejectReason.trim()) {
      setError('Reject requires a reason.');
      return;
    }
    try {
      await api.decideApproval(n.source_id, 'reject', rejectReason);
      setNotifications((prev) => prev.filter((p) => p.source_id !== n.source_id));
      setRejectingId(null);
      setRejectReason('');
    } catch (e) {
      setError(parseApiError(e, 'Reject failed.'));
    }
  };

  const pending = notifications.filter(isPendingApproval);
  const activity = notifications.filter((n) => !isPendingApproval(n));

  const renderRow = (n, withActions) => (
    <Box
      key={`${n.source_type}-${n.source_id}`}
      sx={{
        py: 1,
        px: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: n.is_read ? 'transparent' : 'action.hover',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: n.is_read ? 400 : 600 }}>
            {n.title}
          </Typography>
          {n.body && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {n.body}
            </Typography>
          )}
        </Box>
        {withActions && rejectingId !== n.source_id && (
          <>
            {n.link && (
              <Tooltip title="Open">
                <IconButton
                  size="small"
                  onClick={() => {
                    onNavigate?.(n.link);
                    onClose?.();
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Approve">
              <IconButton size="small" color="success" onClick={() => handleApprove(n)}>
                <Check fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject">
              <IconButton size="small" color="error" onClick={() => handleStartReject(n)}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
      {withActions && rejectingId === n.source_id && (
        <Box sx={{ mt: 1 }}>
          <TextField
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (required)"
            size="small"
            fullWidth
            autoFocus
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setRejectingId(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => handleConfirmReject(n)}
            >
              Send
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minWidth: 360, maxWidth: 420, maxHeight: 480, overflow: 'auto' }}>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="fullWidth">
        <Tab label={`Pending (${pending.length})`} />
        <Tab label={`Activity (${activity.length})`} />
      </Tabs>
      <Divider />
      {error && (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!loading && tab === TAB_PENDING && (
        <Box>
          {pending.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
              Nothing waiting for you.
            </Typography>
          ) : (
            pending.map((n) => renderRow(n, true))
          )}
        </Box>
      )}
      {!loading && tab === TAB_ACTIVITY && (
        <Box>
          {activity.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
              No recent activity.
            </Typography>
          ) : (
            activity.map((n) => renderRow(n, false))
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReviewInbox;
